import { Box } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { formatNumber, NumberFormatPreset } from '@procureai/ui-mui';
import { isNil } from 'ramda';
import { Union } from 'ts-toolbelt';

import { NULLISH_CELL_VALUE } from '../../DataGridCustom.types';
import { StatusUnion } from '~/common/types';
import { MaskedValue, MaskedValueVariant } from '~/components/MaskedValue';
import { colorFromStatus } from '~/util/conversion';

export type NumberCellValue = Union.Nullable<number>;

export interface NumberCellProps<R extends GridValidRowModel = any> {
  value: Union.Nullable<NumberCellValue>;
  row?: R;
  preset: NumberFormatPreset;
  status?: Union.Nullable<StatusUnion>;
  isMasked?: boolean;
}

const InnerNumberCell = <R extends GridValidRowModel = any>({ value, status, preset }: NumberCellProps<R>) => {
  if (!isNil(value)) {
    const statusColor = status ? colorFromStatus(status) : undefined;
    return (
      <Box
        component="span"
        sx={{
          color: statusColor && `${statusColor}.main`,
        }}
      >
        {formatNumber(preset, value)}
      </Box>
    );
  }
  return <>{NULLISH_CELL_VALUE}</>;
};

const presetToMaskedVariant: { [key in NumberFormatPreset]?: MaskedValueVariant } = {
  [NumberFormatPreset.DECIMAL]: MaskedValueVariant.DECIMAL,
  [NumberFormatPreset.CURRENCY]: MaskedValueVariant.CURRENCY,
  [NumberFormatPreset.PERCENT]: MaskedValueVariant.PERCENTAGE,
  [NumberFormatPreset.PERCENT_DECIMAL]: MaskedValueVariant.PERCENTAGE,
};

export const NumberCell = <R extends GridValidRowModel = any>({
  value,
  status,
  preset,
  isMasked,
}: NumberCellProps<R>) => {
  return (
    <MaskedValue isMasked={isMasked} variant={presetToMaskedVariant[preset]}>
      <InnerNumberCell value={value} status={status} preset={preset} />
    </MaskedValue>
  );
};
